import { PMS_REPORT_TYPES, type PMSReportType } from './reportTypes';

export type PortfolioReportViewRoute = 'portfolio-management' | 'operations';

/** List-page paths for PMS Full and PMS Lite report tabs. */
export const PORTFOLIO_REPORTS_BASE_PATHS = [
  '/portfolio/positions/reports',
  '/portfolio/portfolio-management/reports',
  '/operations/treasury-management/operational-reports',
  '/portfolio/operations/reports',
] as const;

export function isPMSReportType(value: string): value is PMSReportType {
  return (PMS_REPORT_TYPES as readonly string[]).includes(value);
}

export function getPortfolioReportsBasePath(viewRoute: PortfolioReportViewRoute): string {
  return `/portfolio/${viewRoute}/reports`;
}

export function getReportsBasePathForPathname(pathname: string): string | undefined {
  return PORTFOLIO_REPORTS_BASE_PATHS.find(base => pathname === base || pathname.startsWith(`${base}/`));
}

/** True when pathname is an open report (run or saved), not the reports list. */
export function isActivePortfolioReportPath(pathname: string, reportsBasePath: string): boolean {
  return pathname.startsWith(`${reportsBasePath}/run/`) || pathname.startsWith(`${reportsBasePath}/saved/`);
}

/** True when pathname is an open report detail route in any portfolio/operations reports tab. */
export function isPortfolioReportDetailPath(pathname: string): boolean {
  return PORTFOLIO_REPORTS_BASE_PATHS.some(base => isActivePortfolioReportPath(pathname, base));
}

export function getPortfolioReportRunRoute(reportsBasePath: string, reportType: PMSReportType): string {
  return `${reportsBasePath}/run/${reportType}`;
}

export function getPortfolioReportSavedRoute(reportsBasePath: string, reportId: string): string {
  return `${reportsBasePath}/saved/${reportId}`;
}

export type ParsedPortfolioReportRoute =
  | { mode: 'list' }
  | { mode: 'run'; reportType: PMSReportType }
  | { mode: 'saved'; reportId: string };

export function parsePortfolioReportRoute(
  pathname: string,
  viewRoute?: PortfolioReportViewRoute
): ParsedPortfolioReportRoute | null {
  const basePath =
    getReportsBasePathForPathname(pathname) ?? (viewRoute != null ? getPortfolioReportsBasePath(viewRoute) : undefined);

  if (basePath == null) {
    return null;
  }

  if (pathname === basePath) {
    return { mode: 'list' };
  }

  const runPrefix = `${basePath}/run/`;
  if (pathname.startsWith(runPrefix)) {
    const reportType = pathname.slice(runPrefix.length);
    if (!reportType.includes('/') && isPMSReportType(reportType)) {
      return { mode: 'run', reportType };
    }
    return null;
  }

  const savedPrefix = `${basePath}/saved/`;
  if (pathname.startsWith(savedPrefix)) {
    const reportId = pathname.slice(savedPrefix.length);
    if (reportId.length > 0 && !reportId.includes('/')) {
      return { mode: 'saved', reportId };
    }
    return null;
  }

  // Unknown path under reports → treat as invalid so the page can redirect to list
  if (pathname.startsWith(`${basePath}/`)) {
    return null;
  }

  return { mode: 'list' };
}
